import { DatePipe } from '@angular/common';
import { Component, EventEmitter, HostListener, Input, Output } from '@angular/core';
import { MovimentoEstoqueService, ProdutoUnidadeService, SubGrupoEstoqueService } from 'almoxarifado-lib';
import { BaseResourceRptComponent, Coluna, EddyAutoComplete, EspecieAlmoxarifado, Favorecido, FavorecidoService, FuncaoService, GlobalService, LoginContabil, ProdutoUnidade, Relatorio, SaldoEstoqueService, Setor, SetorService, SubGrupoEstoque } from 'eddydata-lib';
import { takeUntil } from 'rxjs/operators';
import * as toastr from 'toastr';

declare var $: any;

@Component({
  selector: 'app-entradas-consolidadas-rpt',
  templateUrl: './entradas-consolidadas-rpt.component.html'
})
export class RelatorioEntradaConsolidadasRptComponent extends BaseResourceRptComponent {

  // ========================================================================
  // ----------------------- DECLARAÇÃO DE VARIAVEIS ------------------------
  // ========================================================================

  @Input() visualizar: boolean = false;
  @Output() visualizarChange: EventEmitter<boolean> = new EventEmitter();
  @Input() compraId: number = 0;

  public login: LoginContabil;

  protected datepipe: DatePipe;

  public ordenacao: { nome: string, coluna: any };
  public listaOrdenacao: { nome: string, coluna: string }[] = [];

  public data_inicial: Date;
  public data_final: Date;

  public geral: boolean = false;
  public geralEstoques: boolean = false;

  public especie: EspecieAlmoxarifado;
  public listEspecies: { id: EspecieAlmoxarifado, nome: string }[];

  public produto_unidade: ProdutoUnidade;
  public produtoUnAutoComplete: EddyAutoComplete<ProdutoUnidade>;

  public setor: Setor;
  public setorAutoComplete: EddyAutoComplete<Setor>;

  public favorecido: Favorecido;
  public favorecidoAutoComplete: EddyAutoComplete<Favorecido>;

  public subGrupo: SubGrupoEstoque;
  public subGrupoAutoComplete: EddyAutoComplete<SubGrupoEstoque>;

  // ========================================================================
  // ------------------------------ CONSTRUTOR ------------------------------
  // ========================================================================

  constructor(protected funcaoService: FuncaoService,
    protected globalService: GlobalService,
    protected saldoService: SaldoEstoqueService,
    private movimentoService: MovimentoEstoqueService,
    private produtoUnService: ProdutoUnidadeService,
    private favorecidoService: FavorecidoService,
    private subGrupoService: SubGrupoEstoqueService,
    private setorService: SetorService) {
    super();
    this.datepipe = new DatePipe('pt');
    this.login = GlobalService.obterSessaoLogin();
  }

  // ========================================================================
  // -------------------------- MÉTODOS ABSTRAÍDOS --------------------------
  // ========================================================================

  protected afterInit(): void {
    this.orientacao = 'landscape';
    this.listEspecies = this.globalService.obterEspeciesAlmoxarifado('E');
    this.listaOrdenacao = this.obterColunasOrdenacoes();
    this.ordenacao = this.listaOrdenacao[0];
    this.carregarAutoCompletes();
  }

  protected carregarLista(): Promise<any[]> {
    if (!this.validar())
      return;

    let parametros = {
      'orgao.id': this.login.orgao.id,
      excluido: false,
      entrada: true,
      'OR': `status$null=true;!;!;status$ne=R`,
      relations: ['favorecido', 'recebedor', 'compra', 'itens', 'itens.produto_unidade.produto.material.sub_grupo', 'itens.produto_unidade.unidade'].join(','),
      orderBy: `itens.produto_unidade.produto.material.sub_grupo.nome,${this.ordenacao.coluna}`,
    };

    if (this.produto_unidade) {
      parametros['itens.produto_unidade.id'] = this.produto_unidade.id
    } else if (this.subGrupo) {
      parametros['itens.produto_unidade.produto.material.sub_grupo.id'] = this.subGrupo.id;
    }

    if (this.favorecido) parametros['favorecido.id'] = this.favorecido.id;
    if (this.especie)
      parametros['especie'] = this.especie;

    parametros['data_movimento$ge'] = new FuncaoService().converteDataSQL(this.data_inicial);
    parametros['data_movimento$le'] = new FuncaoService().converteDataSQL(this.data_final);

    if (!this.geralEstoques) {
      parametros['setorAlmoxarifado.estoque.id'] = this.login.setorAlmoxarifado.estoque.id;

      if (!this.geral) {
        this.setor ? parametros['setorAlmoxarifado.setor.id'] = this.setor.id : parametros['setorAlmoxarifado.id'] = this.login.setorAlmoxarifado.id;
      }
    }

    return new Promise((resolve) => {
      this.movimentoService.extendido(1, -1, parametros)
        .pipe(takeUntil(this.unsubscribe))
        .subscribe(lista => {
          if (this.produto_unidade) {
            lista.content.map(entrada => {
              let valor = 0;
              for (const itens of entrada.itens) {
                if (itens['produto_unidade']['produto'].nome === this.produto_unidade.produto.nome) {
                  valor += itens.qtde * itens.valor_unitario;
                  entrada['sub_grupo_nome'] = itens.produto_unidade.produto.material.sub_grupo.nome;
                  entrada['qtde'] = itens.qtde;
                  entrada['valor_unitario'] = itens.valor_unitario;
                }
              }
              entrada['valor_total_saida_item'] = valor;
              return entrada;
            });
            resolve(lista.content);
          } else if (this.subGrupo) {
            const list = [];
            lista.content.map(entrada => {
              for (const item of entrada.itens) {
                const entity = entrada;
                entity['sub_grupo_nome'] = item.produto_unidade.produto.material.sub_grupo.nome;
                entity['qtde'] = item.qtde;
                entity['produto_unidade'] = item.produto_unidade;
                entity['valor_unitario'] = item.valor_unitario;
                entity['valor_total'] = +item.qtde * +item.valor_unitario;
                list.push({ ...entity });
              }
            });
            resolve(list);
          } else {
            lista.content.map(entrada => {
              entrada['sub_grupo_nome'] = entrada.itens[0].produto_unidade.produto.material.sub_grupo.nome;
              entrada.qtde = entrada.itens.reduce((acc, item) => +item.qtde + +acc, 0);
            });
            resolve(lista.content);
          }
        }, () => alert('erro ao retornar lista'));
    });
  }

  protected tituloRelatorio(): string {
    let titulo: string
    const porFornecedor = this.favorecido ? ' - POR FORNECEDOR' : '';
    if (this.produto_unidade) {
      const periodo_inicio = this.data_inicial.toLocaleDateString('pt-BR');
      const periodo_final = this.data_final.toLocaleDateString('pt-BR');
      titulo = `RELATÓRIO DE ENTRADA DE MATERIAIS${porFornecedor}\n   Produto: ${this.produto_unidade.produto.codigo} - ${this.produto_unidade.produto.nome}  -  Período: ${periodo_inicio} - ${periodo_final} `;
    } else {
      titulo = `RELATÓRIO DE ENTRADA DE MATERIAIS${porFornecedor}\n   ${this.subGrupo ? `Subgrupo: ${this.subGrupo.nome}` : ''}`;
    }
    return titulo
  }

  protected obterColunasRelatorio(): Coluna[] {
    const retorno: Coluna[] = []

    if (this.subGrupo) {
      retorno.push({ titulo: 'Grupo Padrão', coluna: 'sub_grupo_nome', alignment: 'left', agrupar: true, bold: true, adicionaisBody: { border: [false, false, false, false] } });
      retorno.push({ titulo: 'Data', coluna: 'data_movimento', alignment: 'center', adicionaisBody: { border: [true, true, true, true] } });
      retorno.push({ titulo: 'CÓDIGO MATERIAL', coluna: 'produto_unidade.produto.codigo', alignment: 'center', adicionaisBody: { border: [true, true, true, true] } });
      retorno.push({ titulo: 'MATERIAL', coluna: 'produto_unidade.produto.nome', adicionaisBody: { border: [true, true, true, true] } });
      retorno.push({ titulo: 'FORNECEDOR', coluna: 'favorecido.nome', adicionaisBody: { border: [true, true, true, true] } });
      retorno.push({ titulo: 'ESPÉCIE', coluna: 'especie', alignment: 'center', adicionaisBody: { border: [true, true, true, true] } });
      retorno.push({ titulo: 'UNIDADE', coluna: 'produto_unidade.unidade.nome', alignment: 'center', adicionaisBody: { border: [true, true, true, true] } });
      retorno.push({ titulo: 'QT.', coluna: 'qtde', alignment: 'center', decimais: 4, adicionaisBody: { border: [true, true, true, true] } });
      retorno.push({ titulo: 'VALOR UN.', coluna: 'valor_unitario', alignment: 'right', decimais: 2 });
      retorno.push({ titulo: 'VALOR TOTAL', coluna: 'valor_total', alignment: 'right', decimais: 2, adicionaisBody: { border: [true, true, true, true] } });
    } else if (this.produto_unidade) {
      retorno.push({ titulo: 'Grupo Padrão', coluna: 'sub_grupo_nome', alignment: 'left', agrupar: true, bold: true, adicionaisBody: { border: [false, false, false, false] } });
      retorno.push({ titulo: 'Data', coluna: 'data_movimento', alignment: 'center', adicionaisBody: { border: [true, true, true, true] } });
      retorno.push({ titulo: 'Nº. OF.', coluna: 'compra.numero', alignment: 'center', adicionaisBody: { border: [true, true, true, true] } });
      retorno.push({ titulo: 'FORNECEDOR', coluna: 'favorecido.nome', adicionaisBody: { border: [true, true, true, true] } });
      retorno.push({ titulo: 'CNPJ', coluna: 'favorecido.cpf_cnpj', alignment: 'center', adicionaisBody: { border: [true, true, true, true] } });
      retorno.push({ titulo: 'DOCUMENTO', coluna: 'documento', alignment: 'center', adicionaisBody: { border: [true, true, true, true] } });
      retorno.push({ titulo: 'RECEBEDOR', coluna: 'recebedor.nome', adicionaisBody: { border: [true, true, true, true] } });
      retorno.push({ titulo: 'ESPÉCIE', coluna: 'especie', alignment: 'center', adicionaisBody: { border: [true, true, true, true] } });
      retorno.push({ titulo: 'QT.', coluna: 'qtde', alignment: 'center', decimais: 4, adicionaisBody: { border: [true, true, true, true] } });
      retorno.push({ titulo: 'VALOR UN.', coluna: 'valor_unitario', alignment: 'right', decimais: 2 });
      retorno.push({ titulo: 'VALOR TOTAL', coluna: 'valor_total_saida_item', alignment: 'right', decimais: 2, adicionaisBody: { border: [true, true, true, true] } });
    } else {
      retorno.push({ titulo: 'Grupo Padrão', coluna: 'sub_grupo_nome', alignment: 'left', agrupar: true, bold: true, adicionaisBody: { border: [false, false, false, false] } });
      retorno.push({ titulo: 'Data', coluna: 'data_movimento', alignment: 'center', adicionaisBody: { border: [true, true, true, true] } });
      retorno.push({ titulo: 'Nº. OF.', coluna: 'compra.numero', alignment: 'center', adicionaisBody: { border: [true, true, true, true] } });
      retorno.push({ titulo: 'FORNECEDOR', coluna: 'favorecido.nome', adicionaisBody: { border: [true, true, true, true] } });
      retorno.push({ titulo: 'CNPJ', coluna: 'favorecido.cpf_cnpj', alignment: 'center', adicionaisBody: { border: [true, true, true, true] } });
      retorno.push({ titulo: 'DOCUMENTO', coluna: 'documento', alignment: 'center', adicionaisBody: { border: [true, true, true, true] } });
      retorno.push({ titulo: 'RECEBEDOR', coluna: 'recebedor.nome', adicionaisBody: { border: [true, true, true, true] } });
      retorno.push({ titulo: 'ESPÉCIE', coluna: 'especie', alignment: 'center', adicionaisBody: { border: [true, true, true, true] } });
      retorno.push({ titulo: 'QT.', coluna: 'qtde', alignment: 'center', decimais: 4, adicionaisBody: { border: [true, true, true, true] } });
      retorno.push({ titulo: 'VALOR', coluna: 'valor_total', alignment: 'right', decimais: 2, adicionaisBody: { border: [true, true, true, true] } });
    }

    return retorno;
  }

  protected larguraColunas(): (string | number)[] {
    let larguras = [];

    if (this.subGrupo) {
      larguras = ['auto', 'auto', '*', 'auto', 'auto', 'auto', 'auto', 'auto', 'auto'];
    } else if (this.produto_unidade) {
      larguras = ['auto', 'auto', '*', 'auto', 'auto', '*', 'auto', 'auto', 'auto', 'auto'];
    } else {
      larguras = ['auto', 'auto', '*', 'auto', 'auto', '*', 'auto', 'auto', 'auto'];
    }

    return larguras;
  }

  protected totalizarColunas(): (string | {})[] {
    if (this.produto_unidade) {
      return [{ nome: 'qtde', abaixo: true }, { nome: 'valor_total_saida_item', abaixo: true }];
    } else {
      return [{ nome: 'qtde', abaixo: true }, { nome: 'valor_total', abaixo: true }];
    }
  }

  protected layout(): {} {
    return {
      padrao: {
        hLineWidth(i, node) {
          return 1;
        },
        vLineWidth(i) {
          return 1;
        },
        hLineColor(i) {
          return 'black';
        },
        paddingLeft(i) {
          return 3;
        },
        paddingRight(i, node) {
          return 3;
        }
      }
    };
  }

  protected conteudoAdicional(lista): {}[] {
    const conteudo: any[] = [];

    conteudo.push([{ text: 'VALOR POR ESPÉCIE', colSpan: 2, alignment: 'center', bold: true, fontSize: 13 }, { text: '' }]);

    let especies: { descricao: string, valor: number }[] = [];
    let valor_total = 0;
    if (this.produto_unidade) {
      for (let movimento of lista) {
        let especie = especies.find((e) => e.descricao === movimento.especie);
        if (especie)
          especie.valor += +movimento.valor_total_saida_item;
        else
          especies.push({ descricao: movimento.especie, valor: +movimento.valor_total_saida_item })
        valor_total += +movimento.valor_total_saida_item;
      }

      for (let especie of especies) {
        if (conteudo.length === 1)
          conteudo.push([{
            text: [
              { text: this.globalService.tituloEspecieAlmoxarifado(<any>especie.descricao) + '  ', bold: true, fontSize: 13, alignment: 'center' }, { text: new FuncaoService().convertToBrNumber(especie.valor), fontSize: 12 }
            ]
          }, {
            text: [
              { text: 'TOTAL  ', bold: true, fontSize: 12, alignment: 'center' }, { text: new FuncaoService().convertToBrNumber(valor_total), fontSize: 12, margin: [10, 0] }
            ], rowSpan: especies.length
          }]);
        else
          conteudo.push([{
            text: [
              { text: this.globalService.tituloEspecieAlmoxarifado(<any>especie.descricao) + '  ', bold: true, fontSize: 12, alignment: 'center' }, { text: new FuncaoService().convertToBrNumber(especie.valor), fontSize: 12, margin: [10, 0] }
            ]
          }, { text: '' }]);
      }
    } else {
      for (let movimento of lista) {
        let especie = especies.find((e) => e.descricao === movimento.especie);
        if (especie)
          especie.valor += +movimento.valor_total;
        else
          especies.push({ descricao: movimento.especie, valor: +movimento.valor_total })
        valor_total += +movimento.valor_total;
      }

      for (let especie of especies) {
        if (conteudo.length === 1)
          conteudo.push([{
            text: [
              { text: this.globalService.tituloEspecieAlmoxarifado(<any>especie.descricao) + '  ', bold: true, fontSize: 13, alignment: 'center' }, { text: new FuncaoService().convertToBrNumber(especie.valor), fontSize: 12 }
            ]
          }, {
            text: [
              { text: 'TOTAL  ', bold: true, fontSize: 12, alignment: 'center' }, { text: new FuncaoService().convertToBrNumber(valor_total), fontSize: 12, margin: [10, 0] }
            ], rowSpan: especies.length
          }]);
        else
          conteudo.push([{
            text: [
              { text: this.globalService.tituloEspecieAlmoxarifado(<any>especie.descricao) + '  ', bold: true, fontSize: 12, alignment: 'center' }, { text: new FuncaoService().convertToBrNumber(especie.valor), fontSize: 12, margin: [10, 0] }
            ]
          }, { text: '' }]);
      }
    }

    return [{
      alignment: 'justify',
      columns: [{
        layout: 'linhas',
        table: {
          headerRows: 1,
          dontBreakRows: true,
          keepWithHeaderRows: 1,
          widths: ['*', '*'],
          body: conteudo
        }
      }, { text: '' }]
    }];
  }

  @HostListener('window:keydown.control.p', ['$event'])
  public abrirConsultaProduto(event: KeyboardEvent) {
    event.preventDefault();
    $('#dialogProdutoUnBusca').modal('show');
  }

  public callbackProdutoUn(prodUn: ProdutoUnidade) {
    this.produto_unidade = prodUn;
  }

  // ========================================================================
  // -------------------------- MÉTODOS DA CLASSE ---------------------------
  // ========================================================================

  private carregarAutoCompletes() {
    this.produtoUnAutoComplete = new EddyAutoComplete(null, this.produtoUnService,
      'id', ['produto.codigo', 'produto.nome', 'unidade.nome'], { orderBy: 'produto.codigo,produto.nome', relations: ['produto.material.sub_grupo.grupo', 'unidade'] }, { number: ['id', 'produto.codigo'], text: ['produto.codigo', 'produto.nome', 'produto_codigo_comp'] },
      null, null, (entidade: ProdutoUnidade) => `${entidade.produto.material.sub_grupo.grupo.codigo}.${entidade.produto.material.sub_grupo.codigo}.${entidade.produto.material.codigo}.${entidade.produto.codigo} - ${entidade.produto.nome}`
    );

    this.setorAutoComplete = new EddyAutoComplete(null, this.setorService,
      'id', ['codigo', 'nome'], { orgao_id: this.login.orgao.id, relations: 'orgao', orderBy: 'nome' }, { text: ['nome', 'codigo'] }
    );

    this.favorecidoAutoComplete = new EddyAutoComplete(null, this.favorecidoService,
      'id', ['cpf_cnpj', 'nome'], { cidade_id: this.login.cidade.id, orderBy: 'nome' }, { number: ['cpf_cnpj'], text: ['nome'] }
    );

    this.subGrupoAutoComplete = new EddyAutoComplete(null, this.subGrupoService,
      'id', ['codigo', 'nome'], { 'orgao_id': this.login.orgao.id, relations: 'grupo', orderBy: 'grupo.codigo,codigo,nome' }, { number: ['codigo'], text: ['codigo', 'nome'] }
    );
  }

  public obterColunasOrdenacoes(): { nome: string, coluna: string }[] {
    return [
      { coluna: 'data_movimento', nome: 'Data Movimento' },
      { coluna: 'favorecido.nome', nome: 'Fornecedor' },
      { coluna: 'compra.numero', nome: 'OF.' },
      { coluna: 'recebedor.nome', nome: 'Recebedor' }
    ];
  }

  private validar(): boolean {
    if (!this.data_final || !this.data_inicial) {
      toastr.warning('Informe o período de conferência de entrada!');
      return false;
    }
    return true;
  }

  public onHide() {
    this.visualizar = false;
    this.visualizarChange.emit(this.visualizar);
  }

  public show() {
    this.visualizar = true;
    this.visualizarChange.emit(this.visualizar);
  }

}